/**
 * 権限チェック用カスタムフック
 *
 * メニューの min_role_view / min_role_edit と
 * ログインユーザーの permissionLevel を比較して、
 * ページの閲覧・編集権限を判定する。
 */
import { useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import type { RootState } from "../app/store";
import type { MenuResponse } from "../api/menus";

interface PermissionResult {
    /** ユーザーの権限レベル (0-9999)。未ログインなら 0 */
    permissionLevel: number;
    /** 現在のページ（またはpath指定ページ）を閲覧できるか */
    canView: boolean;
    /** 現在のページ（またはpath指定ページ）を編集できるか */
    canEdit: boolean;
    /** 任意のパスに対して閲覧権限があるか判定する関数 */
    canViewPath: (path: string) => boolean;
    /** 任意のパスに対して編集権限があるか判定する関数 */
    canEditPath: (path: string) => boolean;
}

/** メニューに対して閲覧権限があるか */
function hasViewAccess(menu: MenuResponse, permissionLevel: number): boolean {
    return permissionLevel >= menu.min_role_view;
}

/** メニューに対して編集権限があるか */
function hasEditAccess(menu: MenuResponse, permissionLevel: number): boolean {
    return permissionLevel >= menu.min_role_edit;
}

/**
 * @param path 省略時は現在のルートパスを使用
 */
export function usePermission(path?: string): PermissionResult {
    const { user } = useAuth();
    const location = useLocation();
    const targetPath = path ?? location.pathname;
    const menus = useSelector((state: RootState) => state.menu.items);

    const permissionLevel = user?.permissionLevel ?? 0;

    const currentMenu = useMemo(
        () => menus.find((m) => m.url === targetPath),
        [menus, targetPath],
    );

    const canView = currentMenu
        ? hasViewAccess(currentMenu, permissionLevel)
        : true;

    const canEdit = currentMenu
        ? hasEditAccess(currentMenu, permissionLevel)
        : true;

    const canViewPath = useCallback(
        (p: string): boolean => {
            const m = menus.find((item) => item.url === p);
            return m ? hasViewAccess(m, permissionLevel) : true;
        },
        [menus, permissionLevel],
    );

    const canEditPath = useCallback(
        (p: string): boolean => {
            const m = menus.find((item) => item.url === p);
            return m ? hasEditAccess(m, permissionLevel) : true;
        },
        [menus, permissionLevel],
    );

    return {
        permissionLevel,
        canView,
        canEdit,
        canViewPath,
        canEditPath,
    };
}
